import { todayISO, PROGRESS_FIELDS, GOALS_FIELD } from "@/lib/format";

const activeNames = (list) => (list || []).filter((o) => o.active).map((o) => o.name);
const allNames = (list) => (list || []).map((o) => o.name);

const churchField = { key: "church_id", label: "Church", type: "church", required: true };
const paymentField = { key: "payment_type", label: "Payment type", type: "choice", required: true, options: (c) => c.settings?.payment_types || [] };
const dateField = { key: "date", label: "Date", type: "date", required: true };
const notesField = { key: "notes", label: "Notes (optional)", type: "textarea" };
const receiptField = { key: "receipt_id", label: "Receipt screenshot (optional)", type: "receipt" };
const paymentFilter = { key: "payment_type", label: "Payment type", options: (c) => c.settings?.payment_types || [] };

export const RECORD_CONFIGS = {
  tithes: {
    endpoint: "tithes",
    title: "Tithe Reports",
    singular: "Tithe",
    subtitle: "Tithes sent to General Council and MWJK",
    moneyKey: "amount",
    fields: [
      churchField,
      { key: "jemdron", label: "Jemdron (optional)", type: "select", when: (c) => c.feat("jemdron"), options: (c) => activeNames(c.options.jemdron) },
      { key: "destination", label: "Sent to", type: "choice", required: true, options: (c) => activeNames(c.options.destinations) },
      { key: "giver_type", label: "Type", type: "choice", when: (c) => c.feat("giver_types"), options: (c) => activeNames(c.options.giver_types) },
      { key: "pastor_name", label: "Pastor / Church / Guest(s) / Ritel", type: "suggest", suggest: "/tithes/names", placeholder: "Type or pick the name of the pastor, church or person" },
      { key: "amount", label: "Amount ($)", type: "money", required: true },
      paymentField,
      dateField,
      notesField,
      { key: "receipt_ids", label: "Receipt files (optional) · photos or PDFs", type: "receipts" },
    ],
    columns: [
      { key: "date", label: "Date", fmt: "date" },
      { key: "church_name", label: "Church" },
      { key: "jemdron", label: "Jemdron", when: (c) => c.feat("jemdron") },
      { key: "destination", label: "Sent to", fmt: "badge" },
      { key: "giver_type", label: "Type", when: (c) => c.feat("giver_types") },
      { key: "pastor_name", label: "Pastor / Church / Guest(s) / Ritel" },
      { key: "payment_type", label: "Payment" },
      { key: "amount", label: "Amount", fmt: "money" },
    ],
    filters: [{ key: "destination", label: "Sent to", options: (c) => allNames(c.options.destinations) }, { key: "jemdron", label: "Jemdron", when: (c) => c.feat("jemdron"), options: (c) => allNames(c.options.jemdron || []) }, { key: "giver_type", label: "Type", when: (c) => c.feat("giver_types"), options: (c) => allNames(c.options.giver_types) }, paymentFilter],
    defaults: (c) => ({
      date: todayISO(),
      destination: activeNames(c.options.destinations)[0] || "",
      payment_type: c.settings?.payment_types?.[0] || "",
      giver_type: c.feat("giver_types") ? activeNames(c.options.giver_types)[0] || "" : "",
      pastor_name: "",
      church_id: c.myChurch?.id || "",
    }),
    hint: (v, c) => (v.destination === "MWJK" && c?.isAdmin ? { text: "Remember: send the MWJK Progress Report together with the MWJK tithe.", to: "/progress?s=mwjk&new=1", cta: "Open Progress Report" } : null),
  },
  donations: {
    endpoint: "donations",
    title: "Conference Collection",
    groupByChurch: true,
    singular: "Conference Collection",
    subtitle: "Offerings, special collections, fees and conference collections",
    moneyKey: "amount",
    fields: [
      churchField,
      { key: "destination", label: "Section", type: "choice", options: (c) => activeNames(c.options.destinations) },
      { key: "amount", label: "Amount ($)", type: "money", required: true },
      paymentField,
      dateField,
      { key: "category", label: "Category", type: "select", required: true, options: (c) => activeNames(c.options.categories) },
      { key: "collection", label: "Collection (event or drive)", type: "select", options: (c) => activeNames(c.options.collections) },
      notesField,
      { key: "receipt_ids", label: "Receipt files (optional) · photos or PDFs", type: "receipts" },
    ],
    columns: [
      { key: "date", label: "Date", fmt: "date" },
      { key: "church_name", label: "Church" },
      { key: "destination", label: "Section", fmt: "badge" },
      { key: "category", label: "Category", fmt: "badge" },
      { key: "collection", label: "Collection" },
      { key: "payment_type", label: "Payment" },
      { key: "amount", label: "Amount", fmt: "money" },
    ],
    filters: [
      { key: "destination", label: "Section", options: (c) => allNames(c.options.destinations) },
      { key: "category", label: "Category", options: (c) => allNames(c.options.categories) },
      { key: "collection", label: "Collection", options: (c) => allNames(c.options.collections) },
      paymentFilter,
    ],
    defaults: (c) => ({
      date: todayISO(),
      payment_type: c.settings?.payment_types?.[0] || "",
      destination: activeNames(c.options.destinations)[0] || "",
      category: activeNames(c.options.categories)[0] || "",
      collection: "",
      church_id: c.myChurch?.id || "",
    }),
  },
  progress: {
    endpoint: "progress",
    sideScoped: true,
    title: "MWJK Progress Report",
    singular: "Progress Report",
    subtitle: "Submitted together with the MWJK tithe",
    fields: [
      churchField,
      { key: "date", label: "Report date", type: "date", required: true },
      ...PROGRESS_FIELDS.map((f) => ({ key: f.key, label: f.mh, sub: f.en, type: "count" })),
      { key: GOALS_FIELD.key, label: GOALS_FIELD.mh, sub: GOALS_FIELD.en, type: "textarea" },
      notesField,
      receiptField,
    ],
    columns: [
      { key: "date", label: "Date", fmt: "date" },
      { key: "church_name", label: "Church" },
      ...PROGRESS_FIELDS.map((f) => ({ key: f.key, label: f.mh, sub: f.en, fmt: "num" })),
      { key: GOALS_FIELD.key, label: GOALS_FIELD.mh, fmt: "long" },
    ],
    filters: [],
    defaults: (c) => ({ date: todayISO(), church_id: c.myChurch?.id || "" }),
  },
};

export function progressConfig(ctx) {
  const base = RECORD_CONFIGS.progress;
  const pf = ctx.settings?.progress_fields || PROGRESS_FIELDS.map((f) => ({ key: f.key, label: f.mh, desc: f.en, visible: true, custom: false }));
  const g = ctx.settings?.goals_field;
  const gf = g ? { ...base.fields[base.fields.length - 3], label: g.label, sub: g.desc || GOALS_FIELD.en } : base.fields[base.fields.length - 3];
  const gc = g ? { ...base.columns[base.columns.length - 1], label: g.label, sub: g.desc } : base.columns[base.columns.length - 1];
  const fields = [base.fields[0], base.fields[1], ...pf.map((f) => ({ key: f.key, label: f.label, sub: f.desc, type: "count", custom: f.custom, when: () => f.visible })), gf, ...base.fields.slice(-2)];
  const columns = [base.columns[0], base.columns[1], ...pf.filter((f) => f.visible).map((f) => ({ key: f.key, label: f.label, sub: f.desc, fmt: "num", ...(f.custom ? { get: (r) => r.custom?.[f.key] } : {}) })), gc];
  return { ...base, fields, columns };
}

export const recordConfig = (type, ctx) => (type === "progress" ? progressConfig(ctx) : RECORD_CONFIGS[type]);
